import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Wrap = styled.div`
  position: fixed;
  bottom: 22px;
  right: 22px;
  z-index: 1500;

  /* In Arabic (RTL) the layout flips, so anchor to the opposite corner
     and keep clear of the right-hand sidebar. */
  [dir='rtl'] & {
    right: auto;
    left: 22px;
  }

  @media print { display: none; }

  @media (max-width: 640px) {
    bottom: 16px;
    right: 16px;
    [dir='rtl'] & { left: 16px; right: auto; }
  }
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #334155;
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.2px;
  cursor: pointer;
  box-shadow: 0 8px 24px -8px rgba(15, 23, 42, 0.32);
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    background: #ffffff;
    box-shadow: 0 12px 30px -10px rgba(99, 102, 241, 0.4);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.35);
  }

  .current { color: #6366f1; }
`;

const Menu = styled.div`
  position: absolute;
  bottom: calc(100% + 10px);
  right: 0;
  min-width: 168px;
  padding: 6px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  box-shadow: 0 16px 40px -12px rgba(15, 23, 42, 0.28);
  display: flex;
  flex-direction: column;
  gap: 2px;

  transform-origin: bottom right;
  animation: lsPop 0.16s ease;

  [dir='rtl'] & {
    right: auto;
    left: 0;
    transform-origin: bottom left;
  }

  @keyframes lsPop {
    from { opacity: 0; transform: translateY(6px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  border: none;
  background: ${props => (props.$active ? '#eef2ff' : 'transparent')};
  color: ${props => (props.$active ? '#4338ca' : '#475569')};
  font-weight: ${props => (props.$active ? 700 : 600)};
  font-size: 0.84rem;
  padding: 9px 12px;
  border-radius: 9px;
  cursor: pointer;
  text-align: start;
  transition: background 0.13s ease, color 0.13s ease;

  &:hover { background: ${props => (props.$active ? '#e0e7ff' : '#f5f7fa')}; }
  &:focus-visible {
    outline: none;
    background: #f5f7fa;
  }

  .flag {
    font-size: 1rem;
    width: 22px;
    text-align: center;
  }
  .name { flex: 1; text-align: start; }
`;

const LANGS = [
  { code: 'en', short: 'EN', name: 'English', flag: '🇬🇧' },
  { code: 'fr', short: 'FR', name: 'Français', flag: '🇫🇷' },
  { code: 'ar', short: 'ع', name: 'العربية', flag: '🇸🇦' },
];

const GlobalLanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const current = (i18n.language || 'en').split('-')[0];
  const active = LANGS.find(l => l.code === current) || LANGS[0];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const label = t('language.label', 'Language');

  return (
    <Wrap ref={ref}>
      {open && (
        <Menu role="listbox" aria-label={label}>
          {LANGS.map((l) => (
            <Item
              key={l.code}
              type="button"
              role="option"
              aria-selected={current === l.code}
              $active={current === l.code}
              onClick={() => choose(l.code)}
            >
              <span className="flag">{l.flag}</span>
              <span className="name">{l.name}</span>
              {current === l.code && <Check size={15} />}
            </Item>
          ))}
        </Menu>
      )}
      <Trigger
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen(o => !o)}
      >
        <Globe size={17} color="#6366f1" />
        <span className="current">{active.short}</span>
      </Trigger>
    </Wrap>
  );
};

export default GlobalLanguageSwitcher;
